<template>
  <section class="consumption-page">
    <header class="consumption-header">
      <div>
        <h1>{{ $t('account_enterprise_cost_record') }}</h1>
        <p>{{ $t('enterprise_ui_consumption_description') }}</p>
      </div>
      <span v-if="params.pageTotalSize" class="consumption-summary">{{ $t('enterprise_ui_page_summary', { page: params.pageNo, total: params.pageTotalSize }) }}</span>
    </header>

    <div id="fileUseList" class="consumption-table-shell">
      <div class="consumption-table-scroll">
        <div class="consumption-table-header consumption-table-grid">
          <div>{{ $t('account_enterprise_cost_record_table_header_name') }}</div>
          <div>{{ $t('account_enterprise_tranlsate_record_table_header_name') }}</div>
          <div>{{ $t('account_enterprise_table_header_alias') }}</div>
          <div>{{ $t('account_translate_recent_date') }}</div>
          <div>{{ $t('account_enterprise_cost_record_table_header_cost_char') }}</div>
          <div>{{ $t('account_enterprise_table_header_operation') }}</div>
        </div>

        <div v-if="tableData.length" class="consumption-table-body">
          <div v-for="file in tableData" :key="file.id" class="consumption-table-row consumption-table-grid">
            <div class="consumption-file-cell">
              <span class="consumption-file-icon" :style="{ backgroundColor: getConsumptionFileType(file).color }">
                <WorkbenchIcon :name="getConsumptionFileType(file).icon" :size="14" />
              </span>
              <el-tooltip :content="file.origFileName" placement="top">
                <span class="consumption-file-name">{{ file.origFileName }}</span>
              </el-tooltip>
            </div>
            <div class="consumption-text strong-text">{{ file.bizMemberName || '—' }}</div>
            <div class="consumption-text muted-text">{{ file.bizMemberAlias || '—' }}</div>
            <div class="consumption-text muted-text">{{ formatDate(file.createTime) }}</div>
            <div class="consumption-value">
              <strong>{{ Number(file.costCharsNum || 0).toLocaleString() }}</strong>
              <span>{{ $t('enterprise_ui_characters') }}</span>
              <span v-if="file.operateStatus == 2 && file.failReason == 4" class="cancel-settle-tag">
                {{ $t('cancel_record_status') }}
              </span>
            </div>
            <div>
              <button
                v-if="getPermission(file, EnterpriseMemberPermission.FILE_LOOK)"
                class="consumption-operation"
                type="button"
                @click="handleOperation(file)"
              >
                {{ $t('account_enterprise_permission_look') }}
              </button>
              <span v-else class="consumption-operation-empty">—</span>
            </div>
          </div>
        </div>

        <div v-else class="consumption-empty-state">
          <span class="consumption-empty-icon"><WorkbenchIcon name="history" :size="28" /></span>
          <h2>{{ $t('no_data') }}</h2>
          <p>{{ $t('enterprise_ui_consumption_empty') }}</p>
        </div>
      </div>
    </div>

    <footer v-if="tableData.length" class="consumption-footer">
      <span>{{ $t('enterprise_ui_consumption_page_count', { count: tableData.length }) }}</span>
      <el-pagination
        v-model:current-page="params.pageNo"
        :page-size="params.pageSize"
        :background="false"
        layout="prev, pager, next"
        :page-count="params.pageTotalSize"
        :pager-count="getPagerCount"
        @current-change="handleCurrentChange"
      />
    </footer>
  </section>
</template>

<script setup lang="ts">
import AccountServer from '~/services/AccountServer'
import WorkbenchIcon from '@/components/Workbench/WorkbenchIcon.vue'
import { type FileItem } from '~/services/AccountServer/types'
import EventBus from 'vue3-eventbus'
import { NetCode } from '~/types/const'
import useUserStore from '~/store/modules/user'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { PermissionUtils } from '~/services/AccountServer/types'
import { EnterpriseMemberPermission } from '~/services/AccountServer/types'
import { isExcelTranslationExtension } from '@/utils/translationFileType'
import { getReadModeArtifactKey, isReadModeRecord } from '@/utils/enterpriseTranslate'
const route = useRoute()
const router = useRouter()
const tableData = ref<FileItem[]>([])
const userStore = useUserStore()
const { userInfo } = storeToRefs(userStore)
const {t} = useI18n()
const params = reactive({
  pageSize: 10,
  pageNo: 1,
  pageTotalSize: 0,
  recordIds: null as number[] | null,
  prevPageId: null as number | null,
  nextPageId: null as number | null
})

watch(userInfo, (newVal)=>{
  getEnterpriseInfo()
})

watch(route, (newVal, oldVal)=>{
  if(newVal.query.menu?.includes('consumption')){
    if (userInfo.value && userInfo.value.bizTeamId) {
      requestFileList()
    }
  }
})

// 查看权限
const getPermission = (file: FileItem, permission: number) => {
  return file.operateStatus == 1 && (
    PermissionUtils.hasPermission(userInfo.value.bizMemberpermission || 0, permission)
    || file.userId == userInfo.value.id
    || userInfo.value.bizTeamMemberRole == 1
  )
}

const getConsumptionFileType = (item: FileItem) => {
  const extension = String(item.origFileName || item.fileType || '').split('.').pop()?.toLowerCase()
  if (extension === 'xlsx' || extension === 'xls') return { icon: 'table', color: '#2fb876' }
  if (extension === 'pptx' || extension === 'ppt') return { icon: 'presentation', color: '#ef4b56' }
  if (['png', 'jpg', 'jpeg', 'webp'].includes(extension || '')) return { icon: 'image', color: '#1687f8' }
  if (extension === 'pdf') return { icon: 'file-text', color: '#ef4b56' }
  if (extension === 'docx' || extension === 'doc') return { icon: 'file-text', color: '#4169e1' }
  return { icon: 'file-text', color: '#7a8699' }
}

// 动态计算是否启用折叠（即返回 3）
const getPagerCount = computed(() => {
  return params.pageTotalSize > 3 ? 3 : params.pageTotalSize
})

const handleCurrentChange = (changeValue) => {
  params.pageNo = changeValue;
  requestFileList(false, true)
  document.body.scrollTo({ top: 0, behavior: 'smooth' });
}

const normalizeRecordField = (value: unknown) => {
  if (!value || typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

// 获取消耗记录
function requestFileList(_isInit = false, _fromPageChange = false) {
  let loading;
  let listDom = document.getElementById("fileUseList")
  if (listDom) {
    loading = ElLoading.service({
        lock: true,
        target: listDom
    })
  }
  const param: Record<string, any> = {
    pageSize: 10,
    pageNo: params.pageNo,
    operateStatus: 6 // 伪状态：成功完成 + 用户主动取消且已结算扣费（failReason=4），后端 getListByLoadmore 支持
  }
  if (params.recordIds) {
    param.recordIds = params.recordIds
  }
  param.modeType = 0

  AccountServer.getRequestAccountUseFilePageList(param).then((res) => {
    if (res.code == "1") {
      let list = res.dataList
      if (list && list.length > 0) {

        list.forEach((item) => {
          item.operateTag = normalizeRecordField(item.operateTag) as Record<string, any>
          item.remark = normalizeRecordField(item.remark) as Record<string, any>
          item.transReqJson = normalizeRecordField(item.transReqJson) as Record<string, any>
        })
        params.pageTotalSize = res.totalPageCount ?? 0;
        tableData.value = list
      } else {
        tableData.value = [];
        params.pageTotalSize = 0
      }
    }
  }).catch((error) => {
    Toast(error.message);
  }).finally(() => {
    loading && loading.close()
  })
}

function getEnterpriseInfo(){
  // 被邀请
  if (userInfo.value && userInfo.value.bizTeamId) {
    requestFileList()
  }
}

function handleOperation(file: FileItem) {
  // if (operation == t('account_enterprise_permission_edit')) {
  //   // 编辑成员
  //   createDialogRef.value?.open(2, member)
  // }else if (operation == t('account_enterprise_operation_delete')) {
  //   // 删除成员

  // }
  AccountServer.getAccountFileCompare({ operateRecordId: file.id }).then((result) => {
    if (result.code === "1") {
      let routerPath = '/home/PDFTranslate'
      // 获取当前页面的完整域名
      const origin = window.location.origin;
      // 获取当前路径
      const path = window.location.pathname;
      // 获取第一级路由
      const firstLevelRoute = path.split('/')[1]; // 分割路径并获取第一级路由
      // window.open(href, '_blank');

      if (isReadModeRecord(file)) {
        const resultBlobName = result.data.operateBlobName || file.blobFileName || ''
        const { href } = router.resolve({
          path: '/home/ReadMarkdown',
          query: {
            oldPDF: result.data.sourceBlobName,
            newPDF: resultBlobName,
            operateRecordId: file.id,
            queryKey: getReadModeArtifactKey(resultBlobName),
            filename: file.origFileName,
          },
        })
        const link = document.createElement('a')
        link.setAttribute('href', href)
        link.setAttribute('target', '_blank')
        link.style.display = 'none'
        link.click()
        return
      }

      if (file.fileType == 'docx') {
        routerPath = '/home/WordTranslate'
      } else if (isExcelTranslationExtension(file.fileType)) {
        routerPath = '/home/ExcelTranslate'
      } else if (file.fileType == 'pptx') {
        routerPath = '/home/PPTXTranslate'
      } else if (file.fileType == 'txt' || file.fileType == 'srt') {
        routerPath = '/home/TextTranslate'
      } else if (file.fileType == 'png' || file.fileType == 'jpg' || file.fileType == 'jpeg') {
        routerPath = '/home/ImageTranslate'
      }

      console.log('href===',routerPath)
      const { href } = router.resolve({
        path: routerPath,
        query: { oldPDF: `${result.data.sourceBlobName}`, newPDF: `${result.data.operateBlobName}`, operateRecordId: file.id },
      });
      
      var a = document.createElement('a');
      a.setAttribute('href', href);
      a.setAttribute('target', '_blank');
      a.style.display = 'none';
      a.click();
    }
  }).catch(() => {

  })
}

onUnmounted(()=>{
  EventBus.off('createAccountEnterprise', getEnterpriseInfo)
})

onMounted(()=>{
  getEnterpriseInfo()
  EventBus.on('createAccountEnterprise', getEnterpriseInfo)
})

</script>

<style lang="scss" scoped>
.enterprise-page {
  max-width: 100%;
}

.page-header {
  
}

.enterprise-add-btn {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  .add-btn-text {
    font-size: 12px;
    font-weight: 400;
    color: white;
    cursor: pointer;
    padding: 8px 20px;
    background-color: #2590E1;
    border-radius: 4px;
    &:hover {
      opacity: 0.8;
    }
  }
}

.page-title {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin: 0;
}

.enterprise-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid #BFBFBF;
  margin-top: 40px;
  .table-header {
    display: grid;
    grid-template-columns: 20% 20% 15% 20% 15% 10%;
    padding: 16px 20px;
    border-bottom: 1px solid #BFBFBF;
    font-weight: 400;
    font-size: 12px;
    color: #262626;
    background-color: #EAF5FC;
  }

  .table-body {
    // max-height: 400px;
    // overflow-y: auto;
  }

  .table-not-data {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 253px;
    .icon-icon_blank1 {
      font-size: 48px;
      color: #BFBFBF;
    }
    .no-data-title {
      margin-top: 10px;
      font-size: 12px;
      font-weight: 400;
      color: #BFBFBF;
    }
    .no-data-content {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      width: 130px;
      height: 30px;
      background: #2590E1;
      border-radius: 4px 4px 4px 4px;
      margin-top: 30px;
      cursor: pointer;
      &:hover {
        opacity: 0.8;
      }
      .icon-icon_add_1 {
        font-size: 16px;
        color: #fff;
      }
      .no-data-text {
        font-size: 12px;
        color: #fff;
        margin-left: 6px;
      }
    }
  }

  .table-row {
    display: grid;
    grid-template-columns: 20% 20% 15% 20% 15% 10%;
    padding: 16px 20px;
    border-bottom: 1px solid #f0f0f0;
    align-items: center;
  }

  .table-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #262626;
    font-weight: 400;
  }

  .header-cell {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #262626;
  }

  .checkbox {
    justify-content: center;
  }

  .name {
    display: flex;
    flex-direction: row;
    align-items: center;
    .name-icon {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .file-name {
      font-size: 12px;
      color: #262626;
      font-weight: 400;
      max-width: 80%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .permission {
    display: flex;
    flex-direction: column;
    align-items: start;
    .permissionItems {
      display: flex;
      flex-direction: row;
 
      .permissionItem {
        display: flex;
        flex-direction: row;
        align-items: center;
        .permission-text {
          min-width: 40px;
        }
        .icon-icon_correct {
          color: #34A853;
          font-size: 14px;
          margin-left: 14px;
          margin-right: 6px;
        }
      }
    }
  }

  .operation {
    display: flex;
    flex-direction: row;
    align-items: center;
    .operation-btn {
      display: flex;
      flex-direction: row;
      align-items: center;
      cursor: pointer;
      &:hover {
        opacity: 0.8;
      }
      .operation-btn-text {
        font-size: 12px;
        color: #2590E1;
        font-weight: 400;
      }
    } 
  }
  .status-badge {
    padding: 4px 0px;
    border-radius: 12px;
    font-size: 12px;
    color: #fff;
    
    &.status-translating {
      color: #F09308;
    }
    
    &.status-success {
      color: #595959;
    }
    
    &.status-failed {
      color: #595959;
    }
  }

  .creator, .date {
    font-size: 14px;
    color: #666;
  }
}
.pageNation {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}
.invited-enterprise-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  .enterprise-content-invited {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    .invited-icon {
      width: 80px;
      height: 80px;
      background: #2590E1;
      border-radius: 0px 0px 0px 0px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 80px;
      .icon-icon_enterprise {
        font-size: 48px;
        color: #fff;
      }
    }
    .invited-title {
      font-size: 16px;
      font-weight: 500;
      color: #262626;
      max-width: 256px;
      text-align: center;
      margin-top: 10px;
    }
    .invited-content {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      width: 100%;
      .invited-content-btn {
        width: 256px;
        height: 40px;
        background: #2590E1;
        border-radius: 4px 4px 4px 4px;
        border: 1px solid #2590E1;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 40px;
        cursor: pointer;
        &:hover {
          opacity: 0.8;
        }
        .invited-content-btn-text {
          font-size: 14px;
          font-weight: 400;
          color: #fff;
        }
      }
    }
  }
}

</style>

<style lang="scss" scoped>
.consumption-page {
  --consumption-primary: #2b7de9;
  --consumption-ink: #1f2329;
  --consumption-sub: #51565d;
  --consumption-muted: #98a2b3;
  --consumption-line: #e8ebf0;
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 18px;
  color: var(--consumption-ink);
}

.consumption-header {
  display: flex;
  min-height: 34px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.consumption-header h1 {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  line-height: 24px;
}

.consumption-header p {
  margin: 3px 0 0;
  color: var(--consumption-muted);
  font-size: 11.5px;
  line-height: 17px;
}

.consumption-summary {
  padding-top: 4px;
  color: var(--consumption-muted);
  font-size: 11.5px;
}

.consumption-table-shell {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--consumption-line);
  border-radius: 10px;
  background: #fff;
}

.consumption-table-scroll {
  min-width: 100%;
  overflow-x: auto;
}

.consumption-table-grid {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) 160px 130px 180px 150px 100px;
  min-width: 960px;
  align-items: center;
  gap: 10px;
}

.consumption-table-header {
  padding: 10px 16px;
  background: #f5f9ff;
  color: var(--consumption-sub);
  font-size: 12px;
  font-weight: 600;
}

.consumption-table-row {
  min-height: 58px;
  box-sizing: border-box;
  padding: 11px 16px;
  border-bottom: 1px solid #f2f5f9;
  transition: background-color .16s ease;
}

.consumption-table-row:last-child { border-bottom: 0; }
.consumption-table-row:hover { background: #fbfcfe; }

.consumption-file-cell {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.consumption-file-icon {
  display: inline-flex;
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #fff;
}

.consumption-file-name,
.consumption-text {
  overflow: hidden;
  font-size: 12.5px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.consumption-file-name,
.strong-text { color: var(--consumption-ink); }
.muted-text { color: var(--consumption-muted); }

.consumption-value {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.consumption-value strong {
  color: var(--consumption-ink);
  font-size: 13px;
  font-weight: 700;
}

.consumption-value span {
  color: var(--consumption-muted);
  font-size: 11.5px;
}

.consumption-value .cancel-settle-tag {
  padding: 1px 6px;
  border-radius: 3px;
  background: #f5f5f5;
  color: #8c8c8c;
  line-height: 16px;
}

.consumption-operation {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--consumption-primary);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.consumption-operation:hover { text-decoration: underline; }
.consumption-operation-empty { color: var(--consumption-muted); }

.consumption-empty-state {
  display: flex;
  min-height: 300px;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 32px;
  text-align: center;
}

.consumption-empty-icon {
  display: inline-flex;
  width: 54px;
  height: 54px;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: #f2f6fb;
  color: #aab3c1;
}

.consumption-empty-state h2 {
  margin: 12px 0 0;
  color: var(--consumption-sub);
  font-size: 13px;
  font-weight: 600;
}

.consumption-empty-state p {
  margin: 6px 0 0;
  color: var(--consumption-muted);
  font-size: 12px;
}

.consumption-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--consumption-muted);
  font-size: 11.5px;
}

.consumption-footer :deep(.el-pagination) {
  --el-pagination-button-width: 26px;
  --el-pagination-button-height: 26px;
  --el-pagination-button-disabled-bg-color: #fff;
  --el-pagination-bg-color: #fff;
  --el-pagination-hover-color: var(--consumption-primary);
  padding: 0;
}

.consumption-footer :deep(.btn-prev),
.consumption-footer :deep(.btn-next),
.consumption-footer :deep(.number) {
  min-width: 26px;
  border: 1px solid #e3e7ec;
  border-radius: 6px;
}
</style>
